import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import {
  BusinessErrorInput,
  BusinessErrorResponseMapping,
  BusinessErrorService,
  BusinessErrorSeverity,
} from '@shared-lib/foundry-shared/business-error.service';
import { SessionExpirationService } from '../shared/foundry-auth-services/session-expiration.service';

/**
 * Reads configured business-error fields from existing API responses. No
 * additional HTTP request is made. Monitoring is active only while an alerts
 * UI is mounted on the current JSON-configured screen. Transaction rollbacks
 * always become errors, preventing consumers from displaying save success.
 */
export const businessErrorResponseInterceptor: HttpInterceptorFn = (request, next): Observable<HttpEvent<unknown>> => {
  const alerts = inject(BusinessErrorService);
  const sessionExpiration = inject(SessionExpirationService);

  return next(request).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        if (getRollbackMessage(event.body)) {
          throw new HttpErrorResponse({
            error: event.body,
            headers: event.headers,
            status: event.status,
            statusText: 'Transaction rolled back',
            url: event.url || request.url,
          });
        }
        notifyFromBody(event.body, alerts);
      }
    }),
    catchError((httpError: unknown) => {
      if (httpError instanceof HttpErrorResponse) {
        if (httpError.status === 401) sessionExpiration.showExpiredPage('unauthorized');
        const rollbackMessage = getRollbackMessage(httpError.error);
        if (rollbackMessage) alerts.showApiError(rollbackMessage);
        else notifyFromBody(httpError.error, alerts);
      }
      return throwError(() => httpError);
    }),
  );
};

/** Inspect response envelopes, not arbitrary record/payload data such as audit history. */
export function getRollbackMessage(body: unknown): string | null {
  if (!isRecord(body)) return null;
  if (String(body['commitStatus'] ?? body['commit_status'] ?? '').trim().toUpperCase() === 'COMMIT_ROLLBACK') {
    const error = body['error'];
    const message = [
      typeof error === 'string' ? error : isRecord(error) ? error['message'] : '',
      body['errorMessage'], body['error_message'], body['message'], body['errorStack'], body['error_stack'],
    ].find(value => typeof value === 'string' && value.trim());
    return typeof message === 'string' ? message.trim() : 'The operation failed and was rolled back. Your changes were not saved.';
  }
  for (const key of ['data', 'response', 'result']) {
    const message = getRollbackMessage(body[key]);
    if (message) return message;
  }
  return null;
}

function notifyFromBody(body: unknown, alerts: BusinessErrorService): void {
  if (body instanceof Blob) {
    if (!isJsonBlob(body)) return;
    void body.text().then(text => {
      try {
        notifyFromBody(JSON.parse(text), alerts);
      } catch {
        // A response advertised as text/JSON may still contain a generated file.
      }
    });
    return;
  }
  if (!alerts.responseMonitorActive || !isRecord(body)) return;
  const notifications: BusinessErrorInput[] = [];
  const seen = new Set<string>();

  alerts.responseMappings.forEach(mapping => {
    collectMappedErrors(body, mapping, notifications, seen);
  });
  if (notifications.length) alerts.ingest(notifications);
}

function collectMappedErrors(
  body: Record<string, unknown>,
  mapping: BusinessErrorResponseMapping,
  notifications: BusinessErrorInput[],
  seen: Set<string>,
): void {
  const mappedErrors = mapping.errorsKey ? readValue(body, mapping.errorsKey) : undefined;
  const errors = Array.isArray(mappedErrors)
    ? mappedErrors
    : [];
  if (!matchesErrorStatus(body, mapping, errors.length > 0)) return;

  const timestamp = readString(body, mapping.timestampKey || 'timestamp');
  const message = readString(body, mapping.messageKey || 'error');
  const responseKey = mapping.keyKey ? readErrorKey(body, mapping.keyKey) : '';
  // When keyKey is configured, its value is both the error indicator and the
  // notification title. This prevents successful `{ message: ... }` responses
  // from being surfaced as failures.
  if (message && (!mapping.keyKey || responseKey)) addUnique(notifications, seen, {
    key: responseKey || mapping.alertKey || 'API_ERROR',
    message,
    severity: 'error',
    timestamp,
  });

  errors.forEach(item => {
    if (typeof item === 'string') {
      const itemMessage = item.trim();
      if (itemMessage) addUnique(notifications, seen, {
        key: mapping.alertKey || 'API_ERROR',
        message: itemMessage,
        severity: 'error',
        timestamp,
      });
      return;
    }
    if (!isRecord(item)) return;
    const itemMessage = readString(item, mapping.itemMessageKey || 'message');
    if (!itemMessage) return;
    addUnique(notifications, seen, {
      key: readString(item, mapping.itemKey || 'code') || mapping.alertKey || 'API_ERROR',
      message: itemMessage,
      severity: normalizeSeverity(readString(item, mapping.severityKey || 'severity')),
      timestamp,
    });
  });
}

function matchesErrorStatus(
  body: Record<string, unknown>,
  mapping: BusinessErrorResponseMapping,
  hasErrors: boolean,
): boolean {
  const expectedStatuses = mapping.errorStatusValues?.map(value => String(value).trim().toLowerCase()).filter(Boolean) || [];
  if (!expectedStatuses.length) return true;
  const status = readString(body, mapping.statusKey || 'status').toLowerCase();
  return hasErrors || expectedStatuses.includes(status);
}

function addUnique(notifications: BusinessErrorInput[], seen: Set<string>, notification: BusinessErrorInput): void {
  const signature = `${notification.key}\u0000${notification.message}`;
  if (seen.has(signature)) return;
  seen.add(signature);
  notifications.push(notification);
}

function normalizeSeverity(value: string): BusinessErrorSeverity {
  const severity = value.toLowerCase();
  return severity === 'warning' || severity === 'info' ? severity : 'error';
}

function readString(record: Record<string, unknown>, key: string): string {
  const value = readValue(record, key);
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value).trim()
    : '';
}

function readErrorKey(record: Record<string, unknown>, key: string): string {
  const value = readValue(record, key);
  if (value === false || value === null || value === undefined) return '';
  if (value === true) return 'ERROR';
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';
}

/** Reads both direct keys and dotted paths such as `data.errorMessages`. */
function readValue(record: Record<string, unknown>, key: string): unknown {
  return key.split('.').reduce<unknown>((value, segment) => {
    return isRecord(value) ? value[segment] : undefined;
  }, record);
}

function isJsonBlob(blob: Blob): boolean {
  const type = blob.type.toLowerCase();
  return type.includes('json') || type.startsWith('text/');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
